import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BrandMark } from "@/components/AppShell";
import { ArrowRight, Mail, ShieldCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/patient";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const ROLES = [
  { value: "patient", label: "Patient" },
  { value: "doctor", label: "Doctor" },
  { value: "hospital", label: "Hospital administrator" },
];

const PATIENT_CATEGORIES = [
  { value: "individual", label: "Individual" },
  { value: "senior", label: "Senior citizen" },
  { value: "corporate", label: "Corporate / group insurance" },
  { value: "student", label: "Student" },
];

const DOCTOR_SPECIALTIES = [
  "Cardiologist", "Neurologist", "Oncologist", "Orthopedician", "Pediatrician",
  "Gastroenterologist", "Dermatologist", "Pulmonologist", "Psychiatrist",
  "General Physician", "Ophthalmologist", "Endocrinologist", "Gynaecologist",
  "Dentist", "ENT Specialist", "Radiologist", "Other",
];

const HOSPITAL_CATEGORIES = [
  { value: "multi_specialty", label: "Multi-specialty hospital" },
  { value: "tertiary_hospital", label: "Tertiary care hospital" },
  { value: "single_specialty_clinic", label: "Single-specialty clinic" },
  { value: "diagnostic_chain", label: "Diagnostic chain" },
  { value: "government", label: "Government hospital" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "patient",
    category: "individual", organization: "", specialty: "", registration_number: "",
    consent_terms: false, consent_privacy: false, consent_data_processing: false,
    consent_marketing: false, consent_analytics: false, consent_research: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setBool = (k) => (v) => setForm((f) => ({ ...f, [k]: !!v }));

  // Reset role-specific fields when role changes
  const onRoleChange = (role) => {
    setForm((f) => ({
      ...f,
      role,
      category: role === "patient" ? "individual" : role === "doctor" ? "practicing" : "multi_specialty",
      organization: "",
      specialty: "",
      registration_number: "",
    }));
  };

  const consentsOk = form.consent_terms && form.consent_privacy && form.consent_data_processing;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consentsOk) {
      toast.error("Please accept the Terms, Privacy Policy, and Data Processing consent.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await register(form);
      toast.success("Account created — verify your email to complete sign-in.");
      navigate("/verify-email", {
        replace: true,
        state: { email: res.email, dev_otp: res.dev_otp, email_sent: res.email_sent },
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/6129444/pexels-photo-6129444.jpeg"
          alt="Consultation"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative h-full p-12 flex flex-col justify-between text-primary-foreground">
          <Link to="/"><BrandMark /></Link>
          <div>
            <div className="overline text-primary-foreground/70">Join Arogya Pramana</div>
            <h2 className="mt-3 font-serif text-4xl leading-tight">
              Verified profiles. Bundled outcomes.
            </h2>
            <p className="mt-4 text-primary-foreground/85 max-w-md">
              Every account is verified with an email OTP and (for professionals) an admin credential review — so patients, doctors and hospitals see the same trust picture.
            </p>
          </div>
          <div className="text-xs text-primary-foreground/60">
            NABH-aligned · DPDP-ready · HL7/FHIR compatible
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Link to="/"><BrandMark /></Link></div>
          <div className="overline">Create account</div>
          <h1 className="mt-2 font-serif text-4xl text-primary">Get started in seconds.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Verified logins bundled by category, organization or specialty.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={form.name} onChange={setField("name")} required data-testid="register-name-input" placeholder="Aarav Mehta" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={setField("email")} required data-testid="register-email-input" placeholder="you@example.in" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" minLength={6} value={form.password} onChange={setField("password")} required data-testid="register-password-input" placeholder="At least 6 characters" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">I am a…</Label>
              <Select value={form.role} onValueChange={onRoleChange}>
                <SelectTrigger id="role" data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`register-role-option-${r.value}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Role-specific bundling fields */}
            {form.role === "patient" && (
              <div className="space-y-2" data-testid="patient-fields">
                <Label htmlFor="category">Category</Label>
                <Select value={form.category} onValueChange={setField("category")}>
                  <SelectTrigger id="category" data-testid="register-patient-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PATIENT_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">Used only to personalise your dashboard and group anonymised outcome analytics.</p>
              </div>
            )}

            {form.role === "doctor" && (
              <div className="space-y-4" data-testid="doctor-fields">
                <div className="space-y-2">
                  <Label htmlFor="specialty">Specialty</Label>
                  <Select value={form.specialty} onValueChange={setField("specialty")}>
                    <SelectTrigger id="specialty" data-testid="register-doctor-specialty">
                      <SelectValue placeholder="Select your specialty" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCTOR_SPECIALTIES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organization">Hospital / clinic affiliation</Label>
                  <Input id="organization" value={form.organization} onChange={setField("organization")} data-testid="register-doctor-organization" placeholder="e.g. Kokilaben Hospital" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="registration_number">Medical Council registration number</Label>
                  <Input id="registration_number" value={form.registration_number} onChange={setField("registration_number")} data-testid="register-doctor-mcr" placeholder="MCI / State Council ID" />
                  <p className="text-[11px] text-muted-foreground">Required for professional verification by our admin team.</p>
                </div>
              </div>
            )}

            {form.role === "hospital" && (
              <div className="space-y-4" data-testid="hospital-fields">
                <div className="space-y-2">
                  <Label htmlFor="organization">Hospital / organization name</Label>
                  <Input id="organization" value={form.organization} onChange={setField("organization")} required data-testid="register-hospital-name" placeholder="e.g. Sunshine Multi-specialty Clinic" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Type</Label>
                  <Select value={form.category} onValueChange={setField("category")}>
                    <SelectTrigger id="category" data-testid="register-hospital-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOSPITAL_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="registration_number">NABH / accreditation reference</Label>
                  <Input id="registration_number" value={form.registration_number} onChange={setField("registration_number")} data-testid="register-hospital-nabh" placeholder="NABH ID or 'Applied'" />
                </div>
              </div>
            )}

            <div className="rounded-md border border-border bg-muted/40 p-4 space-y-3" data-testid="dpdp-notice">
              <div className="inline-flex items-center gap-2 overline text-[10px]">
                <ShieldCheck size={11} className="text-primary" /> DPDP Purpose Notice
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                We are a <strong>Data Fiduciary</strong> under India&apos;s Digital Personal Data Protection Act, 2023.
                Below you specify the purposes for which your personal data may be processed. You can update these
                any time from Privacy &amp; Data settings.{" "}
                <Link to="/dpdp" className="text-primary underline">Read your rights →</Link>
              </p>

              <ConsentCheck id="consent-terms" checked={form.consent_terms} onCheckedChange={setBool("consent_terms")} testid="consent-terms">
                <strong>Terms of Service</strong> — I accept the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline">Terms of Service</Link>.
              </ConsentCheck>
              <ConsentCheck id="consent-privacy" checked={form.consent_privacy} onCheckedChange={setBool("consent_privacy")} testid="consent-privacy">
                <strong>Privacy Policy</strong> — I have read the <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Privacy Policy</Link>.
              </ConsentCheck>
              <ConsentCheck id="consent-data" checked={form.consent_data_processing} onCheckedChange={setBool("consent_data_processing")} testid="consent-data-processing">
                <strong>Core service processing</strong> — Process my personal data to deliver Arogya Pramana services.
              </ConsentCheck>

              <div className="pt-2 mt-1 border-t border-border/70 text-[10px] uppercase tracking-wider text-muted-foreground">
                Optional purposes (can be toggled later)
              </div>

              <ConsentCheck id="consent-analytics" checked={form.consent_analytics} onCheckedChange={setBool("consent_analytics")} testid="consent-analytics">
                <strong>Analytics</strong> — Use my usage data to improve product experience.
              </ConsentCheck>
              <ConsentCheck id="consent-marketing" checked={form.consent_marketing} onCheckedChange={setBool("consent_marketing")} testid="consent-marketing">
                <strong>Marketing</strong> — Send occasional product updates by email.
              </ConsentCheck>
              <ConsentCheck id="consent-research" checked={form.consent_research} onCheckedChange={setBool("consent_research")} testid="consent-research">
                <strong>Research</strong> — Include anonymised outcomes in Arogya Pramana research reports.
              </ConsentCheck>
            </div>

            <Button type="submit" className="w-full h-11" disabled={submitting || !consentsOk} data-testid="register-submit-button">
              {submitting ? "Creating…" : (<>Create account <ArrowRight size={16} className="ml-2" /></>)}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex-1 h-px bg-border" />
            <span className="uppercase tracking-widest">or</span>
            <span className="flex-1 h-px bg-border" />
          </div>

          <Button variant="outline" className="w-full h-11 border-primary text-primary hover:bg-primary/5" onClick={loginWithGoogle} data-testid="google-signup-button">
            <Mail size={16} className="mr-2" />
            Continue with Google
          </Button>

          <div className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConsentCheck({ id, checked, onCheckedChange, children, testid }) {
  return (
    <label htmlFor={id} className="flex items-start gap-2 cursor-pointer">
      <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} data-testid={testid} className="mt-0.5" />
      <span className="text-xs text-foreground leading-relaxed">{children}</span>
    </label>
  );
}
